import { Role } from 'auth';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { IssuerAddNewNavModal } from '@/modules/issuer-dashboard/components/portfolios/IssuerAddNewNavModal.tsx';
import { IssuerPortfolioAITrackingModal } from '@/modules/issuer-dashboard/components/portfolios/IssuerPortfolioAITrackingModal.tsx';
import { CashMovementsTab } from '@/modules/issuer-dashboard/components/portfolios/Tabs/CashMovementsTab.tsx';
import { PortfolioBankAccountsTab } from '@/modules/issuer-dashboard/components/portfolios/Tabs/PortfolioBankAccountsTab.tsx';
import { PortfoliosTab } from '@/modules/issuer-dashboard/components/portfolios/Tabs/PortfoliosTab.tsx';
import { useIssuerPortfolioOverallBalanceStatisticQuery } from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import {
  BlockStatisticsWithCopy,
  Button,
  ModalProviderContext,
  PageLayout,
  StatisticItem,
  TabItem,
  Tabs,
} from 'ui';
import { AIIcon, ChartBlackIcon } from 'ui/icons';
import { goodFormatNumber } from 'ui/utils';
import { PortfolioModel } from 'api';

enum PortfoliosTabsEnum {
  Portfolios = 'Portfolios',
  Banks = 'Banks',
  CashMovements = 'Cash movements',
}

const PORTFOLIOS_TAB_NAME = 'tab-portfolios';

const IssuerPortfolios = () => {
  const { openModal } = useContext(ModalProviderContext);
  const [params] = useSearchParams();
  const { data: dataOverallBalanceStatistic } = useIssuerPortfolioOverallBalanceStatisticQuery();

  const paramTab = params.get(PORTFOLIOS_TAB_NAME);
  const isValidTab =
    paramTab && Object.values(PortfoliosTabsEnum).includes(paramTab as PortfoliosTabsEnum);
  const [activeTab, setActiveTab] = useState<PortfoliosTabsEnum>(
    isValidTab ? (paramTab as PortfoliosTabsEnum) : PortfoliosTabsEnum.Portfolios,
  );

  const tabsItems = useMemo<TabItem<PortfoliosTabsEnum>[]>(
    () => [
      {
        name: PortfoliosTabsEnum.Portfolios,
        container: <PortfoliosTab />,
      },
      {
        name: PortfoliosTabsEnum.Banks,
        container: <PortfolioBankAccountsTab />,
      },
      {
        name: PortfoliosTabsEnum.CashMovements,
        container: <CashMovementsTab />,
      },
    ],
    [],
  );

  const handleAddNewNAV = useCallback(() => {
    openModal(IssuerAddNewNavModal, {
      portfolio: {
        portfolio_value: dataOverallBalanceStatistic?.data?.total_value,
        id: 1,
      } as PortfolioModel,
      fromPortfolio: true,
    });
  }, [dataOverallBalanceStatistic?.data?.total_value, openModal]);

  return (
    <PageLayout
      title="Custody"
      tabs={tabsItems}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      tabNameParam={PORTFOLIOS_TAB_NAME}
      contentClassName="px-0 py-2"
      headerBottomContent={<HeaderActionStatistics />}
      headerAction={
        <div className="flex items-center gap-4">
          <div className="w-fit">
            <Button
              variant={'secondary'}
              className="py-1 px-2 min-h-8"
              icon={AIIcon}
              onClick={() => {
                openModal(IssuerPortfolioAITrackingModal);
              }}
            >
              AI Tracking
            </Button>
          </div>
          <div className="w-fit">
            <Button
              className="max-w-[160px] py-1 px-2 min-h-8"
              onClick={handleAddNewNAV}
              variant="secondary"
              icon={ChartBlackIcon}
            >
              Add new NAV
            </Button>
          </div>
        </div>
      }
    >
      <Tabs items={tabsItems} activeTab={activeTab} />
    </PageLayout>
  );
};

const HeaderActionStatistics = () => {
  const { registerCurrency } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const { data: dataOverallBalanceStatistic, isFetching } =
    useIssuerPortfolioOverallBalanceStatisticQuery();

  const items = useMemo<StatisticItem[]>(() => {
    return [
      {
        name: 'Total value',
        value: goodFormatNumber(dataOverallBalanceStatistic?.data?.total_value || 0),
        currency: registerCurrency,
      },
      {
        name: 'Total available cash',
        value: goodFormatNumber(dataOverallBalanceStatistic?.data?.total_available_cash || 0),
        currency: registerCurrency,
      },
      {
        name: 'Total asset value',
        value: goodFormatNumber(dataOverallBalanceStatistic?.data?.total_asset_value || 0),
        currency: registerCurrency,
      },
      {
        name: 'PNL',
        value: goodFormatNumber(dataOverallBalanceStatistic?.data?.pnl || 0),
        currency: registerCurrency,
        className:
          (dataOverallBalanceStatistic?.data?.pnl || 0) > 0
            ? 'text-[#16A34A]'
            : (dataOverallBalanceStatistic?.data?.pnl || 0) < 0
            ? 'text-[#DC2626]'
            : 'text-[#24699F]',
      },
    ];
  }, [dataOverallBalanceStatistic?.data, registerCurrency]);

  return <BlockStatisticsWithCopy items={items} isFetching={isFetching} withPaddingX={false} />;
};

export default withPrivateAccess(IssuerPortfolios, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
